
import React, { useEffect, useState } from 'react';
import { TrendingUp, Calendar, Users, DollarSign, FileText, Briefcase, Activity, ArrowUpRight, ArrowDownRight, Bell } from 'lucide-react';
import { adminServiceAPI, UserGrowthData, RevenueByTier, MonthlyRevenue } from '@/features/admin/services/adminService-api';
import AdminDashboardSkeleton from '@/features/admin/components/AdminDashboardSkeleton';
import SimpleChart from '@/features/settings/components/SimpleChart';

interface RecentActivity {
  id: string;
  type: 'user' | 'subscription' | 'payment' | 'invoice' | 'quote';
  action: string;
  description: string;
  timestamp: string;
  user?: string;
  userId?: string;
}

const AdminOverview: React.FC = () => {
    const [stats, setStats] = useState({
        totalSystemRevenue: 0,
        totalClientValue: 0,
        activeUsers: 0,
        totalTransactions: 0,
        totalInvoices: 0,
        totalQuotes: 0,
        totalClients: 0,
        mrr: 0,
        arpu: 0,
        churnRate: 0,
        newUsersThisMonth: 0
    });
    const [userGrowth, setUserGrowth] = useState<UserGrowthData[]>([]);
    const [revenueByTier, setRevenueByTier] = useState<RevenueByTier[]>([]);
    const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);
    const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                setLoading(true);

                // Fetch all data in parallel
                const [adminStats, growthData, tierRevenue, monthlyData, activities] = await Promise.all([
                    adminServiceAPI.getAdminStats(),
                    adminServiceAPI.getUserGrowth(),
                    adminServiceAPI.getRevenueByTier(),
                    adminServiceAPI.getMonthlyRevenue(),
                    adminServiceAPI.getRecentActivities(10)
                ]);

                // Calculate MRR (Monthly Recurring Revenue)
                const mrr = tierRevenue.reduce((sum, tier) => sum + tier.amount, 0);

                // Calculate ARPU (Average Revenue Per User)
                const activeUsers = adminStats.totalUsers || 1;
                const arpu = activeUsers > 0 ? mrr / activeUsers : 0;

                // Calculate churn rate (simplified - would need historical data)
                const churnRate = 2.5;

                // Calculate new users this month
                const newUsersThisMonth = growthData.reduce((sum, week) => sum + week.users, 0);

                setStats({
                    totalSystemRevenue: adminStats.totalRevenue,
                    totalClientValue: adminStats.totalRevenue,
                    activeUsers: adminStats.totalUsers,
                    totalTransactions: adminStats.totalInvoices + adminStats.totalQuotes,
                    totalInvoices: adminStats.totalInvoices,
                    totalQuotes: adminStats.totalQuotes,
                    totalClients: adminStats.totalUsers,
                    mrr,
                    arpu,
                    churnRate,
                    newUsersThisMonth
                });

                setUserGrowth(growthData);
                setRevenueByTier(tierRevenue);
                setMonthlyRevenue(monthlyData);
                setRecentActivities(activities as RecentActivity[]);
            } catch (error) {
                console.error('Failed to load dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData();
    }, []);

    const formatTimestamp = (timestamp: string) => {
        const now = new Date();
        const date = new Date(timestamp);
        const diff = now.getTime() - date.getTime();
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const getActivityIcon = (type: RecentActivity['type']) => {
        switch (type) {
            case 'user': return Users;
            case 'subscription': return TrendingUp;
            case 'payment': return DollarSign;
            case 'invoice': return FileText;
            case 'quote': return FileText;
            default: return Activity;
        }
    };

    const getActivityColor = (type: RecentActivity['type']) => {
        switch (type) {
            case 'user': return 'text-blue-600 bg-blue-50';
            case 'subscription': return 'text-emerald-600 bg-emerald-50';
            case 'payment': return 'text-purple-600 bg-purple-50';
            case 'invoice': return 'text-amber-600 bg-amber-50';
            case 'quote': return 'text-cyan-600 bg-cyan-50';
            default: return 'text-neutral-600 bg-neutral-50';
        }
    };

    if (loading) {
        return <AdminDashboardSkeleton />;
    }

    const roiMultiple = stats.totalSystemRevenue > 0
        ? (stats.totalClientValue / stats.totalSystemRevenue).toFixed(1)
        : '0';

    const maxRevenue = monthlyRevenue.length > 0 ? Math.max(...monthlyRevenue.map(d => d.revenue)) : 0;

    // Prepare chart data
    const revenueChartData = monthlyRevenue.map(d => d.revenue);
    const revenueChartLabels = monthlyRevenue.map(d => d.month);

    const userGrowthChartData = userGrowth.map(d => d.users);
    const userGrowthChartLabels = userGrowth.map(d => d.period);

    return (
        <div className="space-y-5">
            {/* Header */}
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Dashboard Overview</h1>
                <p className="text-gray-500 text-sm mt-0.5">Platform performance metrics and analytics</p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <DollarSign size={14} />
                            <span>Total Revenue</span>
                        </div>
                        <span className="text-emerald-600 text-xs font-medium">+12%</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        ${stats.totalSystemRevenue.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">All time revenue</p>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Calendar size={14} />
                            <span>MRR</span>
                        </div>
                        <span className="text-emerald-600 text-xs font-medium">+8%</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        ${stats.mrr.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Monthly recurring</p>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Users size={14} />
                            <span>Active Users</span>
                        </div>
                        <span className="text-emerald-600 text-xs font-medium">+{stats.newUsersThisMonth}</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        {stats.activeUsers}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Registered users</p>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <TrendingUp size={14} />
                            <span>ARPU</span>
                        </div>
                        <span className="text-gray-400 text-xs font-medium">Stable</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        ${stats.arpu.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Avg per user</p>
                </div>
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center gap-2 text-red-600 text-sm mb-2">
                        <Activity size={14} />
                        <span className="font-medium">Churn Rate</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        {stats.churnRate}%
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Monthly churn</p>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                        <FileText size={14} />
                        <span>Documents</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        {stats.totalInvoices + stats.totalQuotes}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{stats.totalInvoices} invoices, {stats.totalQuotes} quotes</p>
                </div>

                <div className="bg-white border border-gray-200 rounded p-4">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                        <Briefcase size={14} />
                        <span>Clients</span>
                    </div>
                    <p className="text-xl font-semibold text-gray-900">
                        {stats.totalClients}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Total clients</p>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Revenue Trend Chart */}
                <div className="bg-white border border-gray-200 rounded p-5">
                    <div className="mb-4">
                        <h2 className="text-base font-semibold text-gray-900">Revenue Trend</h2>
                        <p className="text-sm text-gray-500">Monthly revenue (6 months)</p>
                    </div>
                    {revenueChartData.length > 0 ? (
                        <SimpleChart data={revenueChartData} labels={revenueChartLabels} color="#6366f1" height={180} />
                    ) : (
                        <p className="text-sm text-gray-400 text-center py-8">No data available</p>
                    )}
                </div>

                {/* User Growth Chart */}
                <div className="bg-white border border-gray-200 rounded p-5">
                    <div className="mb-4">
                        <h2 className="text-base font-semibold text-gray-900">User Growth</h2>
                        <p className="text-sm text-gray-500">New users (4 weeks)</p>
                    </div>
                    {userGrowthChartData.length > 0 ? (
                        <SimpleChart data={userGrowthChartData} labels={userGrowthChartLabels} color="#10b981" height={180} />
                    ) : (
                        <p className="text-sm text-gray-400 text-center py-8">No data available</p>
                    )}
                </div>
            </div>

            {/* Revenue Overview */}
            <div className="bg-white border border-gray-200 rounded p-5">
                <div className="mb-4">
                    <h2 className="text-base font-semibold text-gray-900">Revenue Overview</h2>
                    <p className="text-sm text-gray-500">Monthly revenue breakdown</p>
                </div>

                <div className="space-y-3">
                    {monthlyRevenue.length > 0 ? monthlyRevenue.map((data, index) => (
                        <div key={index} className="space-y-1.5">
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-medium text-gray-700">{data.month}</span>
                                <div className="flex items-center gap-3">
                                    <span className="text-gray-500 text-xs">{data.transactions} tx</span>
                                    <span className="font-semibold text-gray-900">${data.revenue.toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="relative h-6 bg-gray-100 rounded overflow-hidden">
                                <div
                                    className="absolute top-0 left-0 h-full bg-gray-900 rounded transition-all duration-300"
                                    style={{ width: maxRevenue > 0 ? `${(data.revenue / maxRevenue) * 100}%` : '0%' }}
                                />
                            </div>
                        </div>
                    )) : (
                        <p className="text-sm text-gray-400 text-center py-8">No revenue data</p>
                    )}
                </div>
            </div>

            {/* Plan Distribution and Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Plan Distribution */}
                <div className="bg-white border border-gray-200 rounded p-5">
                    <h2 className="text-base font-semibold text-gray-900 mb-1">Plan Distribution</h2>
                    <p className="text-sm text-gray-500 mb-4">Users by subscription tier</p>

                    <div className="space-y-3">
                        {revenueByTier.length > 0 ? revenueByTier.map((data, index) => (
                            <div key={index} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="font-medium text-gray-900">{data.tier}</span>
                                    <span className="text-gray-500 text-xs">({data.users})</span>
                                    <span className="font-semibold text-gray-900">${data.amount.toLocaleString()}</span>
                                </div>
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gray-900 rounded-full transition-all"
                                        style={{ width: `${data.percentage}%` }}
                                    />
                                </div>
                            </div>
                        )) : (
                            <p className="text-sm text-gray-400">No data available</p>
                        )}
                    </div>

                    {revenueByTier.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Total Revenue</span>
                                <span className="text-base font-semibold text-gray-900">
                                    ${revenueByTier.reduce((sum, tier) => sum + tier.amount, 0).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Recent Activity */}
                <div className="bg-white border border-gray-200 rounded p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Recent Activity</h2>
                            <p className="text-sm text-gray-500">Latest events</p>
                        </div>
                        <div className="p-1.5 bg-gray-100 rounded">
                            <Bell size={14} className="text-gray-600" />
                        </div>
                    </div>

                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {recentActivities.map((activity) => {
                            const Icon = getActivityIcon(activity.type);
                            const colorClass = getActivityColor(activity.type);

                            return (
                                <div key={activity.id} className="flex items-start gap-3 p-2.5 hover:bg-gray-50 rounded transition-colors">
                                    <div className={`p-1.5 rounded ${colorClass}`}>
                                        <Icon size={14} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{activity.action}</p>
                                        <p className="text-xs text-gray-500 mt-0.5 truncate">{activity.description}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{formatTimestamp(activity.timestamp)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Platform Performance */}
            <div className="bg-white border border-gray-200 rounded p-5">
                <h2 className="text-base font-semibold text-gray-900 mb-1">Platform Performance</h2>
                <p className="text-sm text-gray-500 mb-4">Key performance indicators</p>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Total GMV</p>
                        <p className="text-lg font-semibold text-gray-900">${stats.totalClientValue.toLocaleString()}</p>
                        <p className="text-xs text-gray-400 mt-0.5">All time</p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Documents</p>
                        <p className="text-lg font-semibold text-gray-900">{stats.totalTransactions}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Invoices & quotes</p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">ROI Multiplier</p>
                        <p className="text-lg font-semibold text-gray-900">{roiMultiple}x</p>
                        <p className="text-xs text-gray-400 mt-0.5">Client ROI</p>
                    </div>

                    <div className="p-3 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Active Users</p>
                        <p className="text-lg font-semibold text-gray-900">{stats.activeUsers}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Registered</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminOverview;
